// ─────────────────────────────────────────────────────────────
// buildDoc.js — Noble Enterprises (tape module) print engine
// Turns a report config object into a jsPDF document.
// KnM layout: header → rule → detail boxes → table → totals
//             → terms → signatures → page numbers
// ─────────────────────────────────────────────────────────────

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  COMPANY,
  LOGO,
  PALETTE,
  LAYOUT,
  FONTS,
  applyFmt,
  FOOTER,
} from './printConfig';

const C = (doc, fn, rgb) => doc[fn](rgb[0], rgb[1], rgb[2]);

// ─── Header (drawn on every page) ────────────────────────────
const drawHeader = (doc, cfg) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const mx = LAYOUT.marginX;
  let y = LAYOUT.headerTop;

  // Logo — image if supplied, otherwise initials box
  if (LOGO.dataUrl) {
    try {
      doc.addImage(LOGO.dataUrl, LOGO.format, mx, y - 4, LOGO.width, LOGO.height);
    } catch (e) {
      // bad/absent image — fall through silently
    }
  } else {
    C(doc, 'setDrawColor', PALETTE.rule);
    doc.setLineWidth(0.5);
    doc.rect(mx, y - 4, LOGO.width, LOGO.height);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    C(doc, 'setTextColor', PALETTE.ink);
    doc.text(LOGO.initials, mx + LOGO.width / 2, y + 3.5, { align: 'center' });
  }

  // Company name + tagline
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  C(doc, 'setTextColor', PALETTE.ink);
  doc.text(COMPANY.name, mx + LOGO.width + 4, y + 1);
  if (COMPANY.tagline) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONTS.tiny);
    C(doc, 'setTextColor', PALETTE.muted);
    doc.text(COMPANY.tagline, mx + LOGO.width + 4, y + 5.5);
  }

  // Title (centre)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONTS.title);
  C(doc, 'setTextColor', PALETTE.navy);
  doc.text(String(cfg.title || '').toUpperCase(), pageWidth / 2, y + 3, {
    align: 'center',
  });

  if (cfg.subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONTS.small);
    C(doc, 'setTextColor', PALETTE.muted);
    doc.text(String(cfg.subtitle), pageWidth / 2, y + 9, { align: 'center' });
  }

  // Address block (right)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONTS.small);
  C(doc, 'setTextColor', PALETTE.muted);
  const rx = pageWidth - mx;
  doc.text(COMPANY.address, rx, y - 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}  |  ${COMPANY.landline}`, rx, y + 2, {
    align: 'right',
  });
  doc.text(`STRN: ${COMPANY.strn}`, rx, y + 6, { align: 'right' });
  doc.text(`NTN: ${COMPANY.ntn}`, rx, y + 10, { align: 'right' });

  y += 16;

  // Bold divider
  C(doc, 'setDrawColor', PALETTE.rule);
  doc.setLineWidth(0.8);
  doc.line(mx, y, pageWidth - mx, y);

  return y + 8; // y below the rule
};

// ─── Detail boxes (page 1 only) ──────────────────────────────
const drawBoxes = (doc, boxes, y) => {
  if (!boxes || boxes.length === 0) return y;

  const pageWidth = doc.internal.pageSize.getWidth();
  const mx = LAYOUT.marginX;
  const count = Math.min(boxes.length, 2);
  const boxW =
    count === 1
      ? pageWidth - mx * 2
      : (pageWidth - mx * 2 - LAYOUT.boxGap) / 2;

  // height grows with the longest box
  const maxRows = Math.max(...boxes.map((b) => (b.rows || []).length));
  const boxH = Math.max(LAYOUT.boxHeight, 11 + maxRows * LAYOUT.rowGap);

  boxes.slice(0, 2).forEach((box, i) => {
    const bx = mx + i * (boxW + LAYOUT.boxGap);

    C(doc, 'setDrawColor', PALETTE.border);
    doc.setLineWidth(0.3);
    doc.rect(bx, y, boxW, boxH);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONTS.boxHeading);
    C(doc, 'setTextColor', PALETTE.heading);
    doc.text(`${box.heading || ''}:`, bx + 3, y + 5);

    const labelX = bx + 3;
    const valX = bx + 32;
    const labelX2 = bx + boxW / 2 + 3;
    const valX2 = bx + boxW / 2 + 26;
    let ry = y + 11;

    (box.rows || []).forEach((r) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONTS.label);
      C(doc, 'setTextColor', PALETTE.inkSoft);
      doc.text(String(r.label || ''), labelX, ry);

      doc.setFont('helvetica', 'normal');
      C(doc, 'setTextColor', PALETTE.ink);
      doc.text(
        String(r.value === null || r.value === undefined || r.value === '' ? '—' : r.value),
        valX,
        ry
      );

      if (r.label2) {
        doc.setFont('helvetica', 'bold');
        C(doc, 'setTextColor', PALETTE.inkSoft);
        doc.text(String(r.label2), labelX2, ry);
        doc.setFont('helvetica', 'normal');
        C(doc, 'setTextColor', PALETTE.ink);
        doc.text(
          String(r.value2 === null || r.value2 === undefined || r.value2 === '' ? '—' : r.value2),
          valX2,
          ry
        );
      }
      ry += LAYOUT.rowGap;
    });
  });

  return y + boxH + 8;
};

// ─── Running balance (ledgers) ───────────────────────────────
// Returns { columns, rows, meta } with the balance column appended
// and an opening row prepended.
const applyRunningBalance = (cfg) => {
  const rb = cfg.runningBalance;
  const columns = [...(cfg.columns || [])];
  const rows = [...(cfg.rows || [])];
  const meta = rows.map((r) => ({ tone: r.__tone || null, kind: 'row' }));

  if (!rb) return { columns, rows, meta };

  const drKey = rb.debitKey || 'debit';
  const crKey = rb.creditKey || 'credit';
  const sign = rb.sign === 'cr-dr' ? -1 : 1;
  const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

  let bal = num(cfg.opening ? cfg.opening.value : 0);

  const outRows = rows.map((r) => {
    bal += sign * (num(r[drKey]) - num(r[crKey]));
    return { ...r, __balance: bal };
  });

  columns.push({
    key: '__balance',
    label: rb.label || 'Balance',
    align: 'right',
    fmt: rb.fmt || 'money',
    width: rb.width || 26,
    bold: true,
  });

  // opening row
  const openRow = { __opening: true, __balance: num(cfg.opening ? cfg.opening.value : 0) };
  openRow[columns[0].key] = cfg.opening ? cfg.opening.label : 'Opening Balance';

  return {
    columns,
    rows: [openRow, ...outRows],
    meta: [{ tone: null, kind: 'opening' }, ...outRows.map((r) => ({ tone: r.__tone || null, kind: 'row' }))],
    closing: bal,
  };
};

// ─── Table ───────────────────────────────────────────────────
const drawTable = (doc, opts) => {
  const {
    columns,
    rows,
    meta,
    startY,
    marginTop,
    footRow,
    headFill,
    onPage,
    zebra,
  } = opts;
  const mx = LAYOUT.marginX;

  const head = [columns.map((c) => c.label)];

  const body =
    rows.length > 0
      ? rows.map((r) =>
          columns.map((c) => {
            // Opening and subtotal rows only fill a few cells.
            // Anything absent prints blank, not a dash or a zero.
            if (r.__opening && c.key !== columns[0].key && c.key !== '__balance') return '';
            // A zero debit against a credit entry is noise, not data
            if (c.blankZero && !r[c.key]) return '';
            if (r.__subtotal && !r[c.key]) return '';
            return applyFmt(r[c.key], c.fmt);
          })
        )
      : [columns.map((_, i) => (i === 0 ? 'No records' : '—'))];

  const columnStyles = {};
  columns.forEach((c, i) => {
    columnStyles[i] = {
      cellWidth: c.width || 'auto',
      halign: c.align || 'left',
      fontStyle: c.bold ? 'bold' : 'normal',
    };
  });

  autoTable(doc, {
    startY,
    head,
    body,
    foot: footRow ? [footRow] : undefined,
    theme: 'grid',
    headStyles: {
      fillColor: headFill || PALETTE.headFill,
      textColor: PALETTE.ink,
      fontStyle: 'bold',
      fontSize: FONTS.body,
      lineColor: PALETTE.border,
      lineWidth: 0.2,
    },
    bodyStyles: {
      fontSize: FONTS.body,
      textColor: PALETTE.ink,
      lineColor: PALETTE.border,
      lineWidth: 0.2,
    },
    footStyles: {
      fillColor: PALETTE.footFill,
      textColor: PALETTE.ink,
      fontStyle: 'bold',
      fontSize: FONTS.body,
      lineColor: PALETTE.border,
      lineWidth: 0.2,
    },
    alternateRowStyles: zebra ? { fillColor: PALETTE.zebra } : undefined,
    columnStyles,
    margin: { left: mx, right: mx, top: marginTop },
    didParseCell: (data) => {
      if (data.section !== 'body' || !meta) return;
      const m = meta[data.row.index];
      if (!m) return;

      if (m.kind === 'opening' || m.kind === 'subtotal') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = PALETTE.grandFill;
        return;
      }
      if (m.tone && PALETTE[m.tone]) {
        data.cell.styles.textColor = PALETTE[m.tone];
      }
      const col = columns[data.column.index];
      if (col && col.tone && PALETTE[col.tone]) {
        data.cell.styles.textColor = PALETTE[col.tone];
      }
    },
    didDrawPage: (data) => {
      if (onPage) onPage(data);
    },
  });

  return doc.lastAutoTable.finalY;
};

// ─── Totals box (right aligned) ──────────────────────────────
const drawTotalsBox = (doc, totals, y) => {
  if (!totals || totals.length === 0) return y;

  const pageWidth = doc.internal.pageSize.getWidth();
  const boxW = LAYOUT.totalsBoxWidth;
  const boxX = pageWidth - LAYOUT.marginX - boxW;

  let ty = y;
  doc.setFontSize(FONTS.totals);

  totals.forEach((t) => {
    const emph = !!t.emphasis;
    if (emph) {
      const fill = t.tone && PALETTE[`${t.tone}Fill`] ? PALETTE[`${t.tone}Fill`] : PALETTE.grandFill;
      C(doc, 'setFillColor', fill);
      doc.rect(boxX, ty - 4, boxW, 7, 'F');
    }
    doc.setFont('helvetica', emph ? 'bold' : 'normal');
    const col = t.tone && PALETTE[t.tone] ? PALETTE[t.tone] : PALETTE.ink;
    C(doc, 'setTextColor', col);
    doc.text(String(t.label), boxX + 2, ty);
    doc.text(applyFmt(t.value, t.fmt || 'money'), boxX + boxW - 2, ty, {
      align: 'right',
    });
    ty += emph ? 7 : 6;
  });

  C(doc, 'setDrawColor', PALETTE.border);
  doc.setLineWidth(0.3);
  doc.rect(boxX, y - 5, boxW, ty - y + 1);

  return ty + 4;
};

// ─── Terms ───────────────────────────────────────────────────
const drawTerms = (doc, terms, y, termsHeading) => {
  if (!terms || terms.length === 0) return y;

  const mx = LAYOUT.marginX;
  const pageHeight = doc.internal.pageSize.getHeight();

  const blockH = 4 + terms.length * 3.6;
  const maxY = pageHeight - (blockH + 20 + 8);
  let ty = Math.min(Math.max(y + 6, y), maxY < y ? y : maxY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONTS.label);
  C(doc, 'setTextColor', PALETTE.inkSoft);
  doc.text(String(termsHeading || 'TERMS & CONDITIONS:'), mx, ty);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONTS.tiny);
  C(doc, 'setTextColor', PALETTE.muted);
  let ly = ty + 4;
  terms.forEach((t) => {
    doc.text(String(t), mx, ly);
    ly += 3.6;
  });

  return ly;
};

// ─── Signatures ──────────────────────────────────────────────
const drawSignatures = (doc, sigs, y) => {
  if (!sigs || sigs.length === 0) return y;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const mx = LAYOUT.marginX;

  let sy = Math.max(y + 14, pageHeight - 22);
  if (sy > pageHeight - LAYOUT.bottomSafe) sy = pageHeight - LAYOUT.bottomSafe;

  const sigW = LAYOUT.signatureWidth;
  const gap =
    sigs.length > 1
      ? (pageWidth - mx * 2 - sigW * sigs.length) / (sigs.length - 1)
      : 0;

  C(doc, 'setDrawColor', PALETTE.inkSoft);
  doc.setLineWidth(0.3);

  sigs.forEach((label, i) => {
    const sx = mx + i * (sigW + gap);
    doc.line(sx, sy, sx + sigW, sy);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONTS.totals);
    C(doc, 'setTextColor', PALETTE.inkSoft);
    doc.text(String(label), sx + sigW / 2, sy + 5, { align: 'center' });
  });

  return sy + 6;
};

// ─── Footer / page numbers (all pages, stamped last) ─────────
const stampFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const mx = LAYOUT.marginX;
  const total = doc.internal.getNumberOfPages();

  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONTS.tiny);
    C(doc, 'setTextColor', PALETTE.faint);
    doc.text(FOOTER.left(), mx, pageHeight - 6);
    doc.text(FOOTER.right(p, total), pageWidth - mx, pageHeight - 6, {
      align: 'right',
    });
  }
};

// ─────────────────────────────────────────────────────────────
// MAIN — buildDoc(config) → jsPDF
// ─────────────────────────────────────────────────────────────
export const buildDoc = (config) => {
  const cfg = config || {};
  const doc = new jsPDF({
    unit: 'mm',
    format: cfg.format || 'a4',
    orientation: cfg.orientation === 'l' ? 'landscape' : 'portrait',
  });

  // page 1 header
  let y = drawHeader(doc, cfg);
  const headerBottom = y; // subsequent pages resume here
  const onPage = (data) => {
    if (data.pageNumber > 1) drawHeader(doc, cfg);
  };

  // detail boxes
  y = drawBoxes(doc, cfg.boxes, y);

  if (cfg.kind === 'statement') {
    // ─── Grouped sections (P&L) ───
    (cfg.sections || []).forEach((sec) => {
      const tone = sec.tone;
      const headFill =
        tone && PALETTE[`${tone}Fill`] ? PALETTE[`${tone}Fill`] : PALETTE.headFill;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FONTS.boxHeading);
      C(doc, 'setTextColor', tone && PALETTE[tone] ? PALETTE[tone] : PALETTE.heading);
      doc.text(String(sec.title || '').toUpperCase(), LAYOUT.marginX, y);
      y += 3;

      const secRows = sec.rows || [];
      const meta = secRows.map((r) => ({
        tone: r.__tone || null,
        kind: r.__subtotal ? 'subtotal' : 'row',
      }));

      const footRow = sec.footRow
        ? sec.footRow
        : sec.total !== undefined
        ? (sec.columns || []).map((c, i) =>
            i === 0
              ? sec.totalLabel || 'Total'
              : i === (sec.columns || []).length - 1
              ? applyFmt(sec.total, c.fmt || 'money')
              : ''
          )
        : undefined;

      y = drawTable(doc, {
        columns: sec.columns || cfg.columns || [],
        rows: secRows,
        meta,
        startY: y,
        marginTop: headerBottom,
        footRow,
        headFill,
        zebra: true,
        onPage,
      });
      y += 8;
    });
  } else {
    // ─── document / list ───
    const { columns, rows, meta, closing } = applyRunningBalance(cfg);

    let footRow = cfg.footRow;
    if (!footRow && cfg.columnTotals) {
      footRow = columns.map((c) => {
        if (c.key === '__balance' && closing !== undefined) {
          return applyFmt(closing, c.fmt || 'money');
        }
        if (!cfg.columnTotals.includes(c.key)) return '';
        const sum = (cfg.rows || []).reduce(
          (s, r) => s + (isNaN(parseFloat(r[c.key])) ? 0 : parseFloat(r[c.key])),
          0
        );
        return applyFmt(sum, c.fmt || 'money');
      });
      if (footRow[0] === '') footRow[0] = cfg.totalLabel || 'TOTAL';
    }

    y = drawTable(doc, {
      columns,
      rows,
      meta,
      startY: y,
      marginTop: headerBottom,
      footRow,
      zebra: cfg.kind === 'list',
      onPage,
    });
    y += 8;
  }

  // totals box
  y = drawTotalsBox(doc, cfg.totals, y);

  // terms + signatures
  y = drawTerms(doc, cfg.terms, y, cfg.termsHeading);
  drawSignatures(doc, cfg.signatures, y);

  stampFooter(doc);
  return doc;
};

// ─── Output helpers ──────────────────────────────────────────
export const saveDoc = (config, filename) => {
  const doc = buildDoc(config);
  doc.save(`${filename || 'report'}.pdf`);
};

export const printDoc = (config) => {
  const doc = buildDoc(config);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};

export default buildDoc;